import * as Sentry from "@sentry/react";
import { getToken, getRefreshToken, setTokens, clearTokens } from "@/lib/auth";
import type {
  ApiResponse,
  AuthResponse,
  User,
  LoginRequest,
  RegisterRequest,
  Exam,
  Question,
  Category,
  AnalyticsSummary,
  CategoryStats,
  WeakQuestion,
  ProgressPoint,
  TestSession,
  UserAnswer,
  CreateTestRequest,
  SubmitAnswerRequest,
  PaginationMeta,
  GenerateQuestionsRequest,
  GenerateQuestionsResult,
  Submission,
  SubmitQuestionRequest,
  ReviewSubmissionRequest,
  Report,
  CreateReportRequest,
  ReviewReportRequest,
  ExamAccessRequest,
  AdminUser,
  RequestExamAccessRequest,
  ReviewExamRequest,
  Group,
  GroupMember,
  GroupInvite,
  CreateGroupRequest,
  UpdateGroupRequest,
  InviteByEmailResult,
  LeaderboardEntry,
  LeaderboardPeriod,
  GroupCapInquiryPayload,
} from "@/lib/types";

const API_BASE =
  import.meta.env.VITE_API_URL || "http://localhost:8080/api/v1";

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// NetworkError wraps a raw fetch() rejection so Sentry shows a useful title
// (method + endpoint) instead of an opaque "TypeError: Load failed".
class NetworkError extends Error {
  constructor(
    public endpoint: string,
    public method: string,
    cause: unknown,
  ) {
    const causeMsg =
      cause instanceof Error ? cause.message : String(cause ?? "fetch failed");
    super(`[${method} ${endpoint}] ${causeMsg}`);
    this.name = "NetworkError";
    this.cause = cause;
  }
}

function captureNetworkError(
  endpoint: string,
  method: string,
  cause: unknown,
): NetworkError {
  const wrapped = new NetworkError(endpoint, method, cause);
  Sentry.captureException(wrapped, {
    tags: { api_endpoint: endpoint, error_type: "network", method },
    extra: { cause: cause instanceof Error ? cause.message : String(cause) },
    fingerprint: ["network", method, endpoint],
  });
  return wrapped;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const token = getToken();
  const method = (options.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
  } catch (cause) {
    throw captureNetworkError(endpoint, method, cause);
  }

  // If 401, attempt token refresh and retry once
  if (res.status === 401 && token) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      headers["Authorization"] = `Bearer ${getToken()}`;
      try {
        res = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
      } catch (cause) {
        throw captureNetworkError(endpoint, method, cause);
      }
    }
  }

  const body: ApiResponse<T> = await res.json();

  if (!body.success) {
    const apiError = new ApiError(res.status, body.error || "Unknown error");
    Sentry.captureException(apiError, {
      tags: {
        api_endpoint: endpoint,
        status_code: String(res.status),
        error_type: "api",
        method,
      },
      extra: { body: body.error },
      fingerprint: ["api", method, endpoint, String(res.status)],
    });
    throw apiError;
  }

  return body;
}

// Single in-flight refresh promise so concurrent 401s don't double-rotate
// the refresh token (which would invalidate one of them on the server).
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const rt = getRefreshToken();
    if (!rt) return false;

    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      const body: ApiResponse<AuthResponse> = await res.json();
      if (body.success && body.data) {
        setTokens(body.data.access_token, body.data.refresh_token);
        return true;
      }
    } catch {
      // Refresh failed silently
    }

    clearTokens();
    return false;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export async function login(data: LoginRequest): Promise<AuthResponse> {
  const res = await request<AuthResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.data!;
}

export async function register(data: RegisterRequest): Promise<void> {
  await request<{ message: string }>("/auth/register", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function resendVerification(email: string): Promise<void> {
  await request("/auth/resend-verification", { method: "POST", body: JSON.stringify({ email }) });
}

export async function getCurrentUser(): Promise<User> {
  const res = await request<User>("/me");
  return res.data!;
}

export async function logout(): Promise<void> {
  const rt = getRefreshToken();
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt ?? "" }),
    });
  } catch {
    // ignore — client must clear local tokens regardless
  }
  clearTokens();
}

export async function refreshToken(): Promise<AuthResponse> {
  const rt = getRefreshToken();
  const res = await request<AuthResponse>("/auth/refresh", {
    method: "POST",
    body: JSON.stringify({ refresh_token: rt }),
  });
  return res.data!;
}

export async function forgotPassword(email: string): Promise<void> {
  await request("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await request("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

// ─── Inquiries ───────────────────────────────────────────────────────────────

export interface ExamInquiryPayload {
  exam_name: string;
  source?: string;
  motivation?: string;
}

export async function submitExamInquiry(payload: ExamInquiryPayload): Promise<void> {
  await request("/inquiries/exam", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type FeedbackCategory = "bug" | "idea" | "other";

export interface FeedbackPayload {
  category: FeedbackCategory;
  message: string;
  path?: string;
}

export async function submitFeedback(payload: FeedbackPayload): Promise<void> {
  await request("/inquiries/feedback", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ─── Exams ───────────────────────────────────────────────────────────────────

/**
 * Returns exams the current user has access to. Admins see all exams.
 * Backend endpoint is assignment-aware; users with no assignments get [].
 */
export async function getExams(): Promise<Exam[]> {
  const res = await request<Exam[]>("/exams");
  return res.data ?? [];
}

/** Returns every exam — used by the request-access flow to pick an exam. */
export async function getAllExams(): Promise<Exam[]> {
  const res = await request<Exam[]>("/exams/all");
  return res.data ?? [];
}

export async function getExam(slug: string): Promise<Exam> {
  const res = await request<Exam>(`/exams/${slug}`);
  return res.data!;
}

// ─── Exam Access Requests / Assignments ─────────────────────────────────────

export async function requestExamAccess(
  examSlug: string,
  data: RequestExamAccessRequest = {},
): Promise<ExamAccessRequest> {
  const res = await request<ExamAccessRequest>(
    `/exams/${examSlug}/access-request`,
    { method: "POST", body: JSON.stringify(data) },
  );
  return res.data!;
}

export async function getMyExamRequests(): Promise<ExamAccessRequest[]> {
  const res = await request<ExamAccessRequest[]>("/my/requests");
  return res.data ?? [];
}

export async function cancelExamAccess(examSlug: string): Promise<void> {
  await request(`/exams/${examSlug}/access-request`, { method: "DELETE" });
}

export async function getAdminUsers(): Promise<AdminUser[]> {
  const res = await request<AdminUser[]>("/admin/users");
  return res.data ?? [];
}

export async function assignExam(userID: string, examSlug: string): Promise<void> {
  await request(`/admin/users/${userID}/exams/${examSlug}`, {
    method: "POST",
  });
}

export async function revokeExam(userID: string, examSlug: string): Promise<void> {
  await request(`/admin/users/${userID}/exams/${examSlug}`, {
    method: "DELETE",
  });
}

export async function getPendingExamRequests(): Promise<ExamAccessRequest[]> {
  const res = await request<ExamAccessRequest[]>("/admin/requests");
  return res.data ?? [];
}

export async function reviewExamRequest(
  userID: string,
  examSlug: string,
  data: ReviewExamRequest,
): Promise<void> {
  await request(`/admin/requests/${userID}/${examSlug}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ─── Questions (exam-scoped) ─────────────────────────────────────────────────

export async function getQuestions(
  examSlug: string,
  page = 1,
  perPage = 20,
  categoryId?: number,
  search?: string,
  filters?: {
    fromNumber?: number;
    toNumber?: number;
    fromDisplayNumber?: number;
    toDisplayNumber?: number;
    isMultiple?: boolean;
    hasExplanation?: boolean;
  },
): Promise<{ questions: Question[]; meta: PaginationMeta }> {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("per_page", String(perPage));
  if (categoryId) params.set("category_id", String(categoryId));
  if (search) params.set("search", search);
  if (filters?.fromNumber != null) params.set("from_number", String(filters.fromNumber));
  if (filters?.toNumber != null) params.set("to_number", String(filters.toNumber));
  if (filters?.fromDisplayNumber != null)
    params.set("from_display_number", String(filters.fromDisplayNumber));
  if (filters?.toDisplayNumber != null)
    params.set("to_display_number", String(filters.toDisplayNumber));
  if (filters?.isMultiple != null) params.set("is_multiple", String(filters.isMultiple));
  if (filters?.hasExplanation != null) params.set("has_explanation", String(filters.hasExplanation));

  const res = await request<Question[]>(`/exams/${examSlug}/questions?${params.toString()}`);
  return { questions: res.data ?? [], meta: res.meta! };
}

export async function getQuestion(examSlug: string, id: number): Promise<Question> {
  const res = await request<Question>(`/exams/${examSlug}/questions/${id}`);
  return res.data!;
}

export async function getRandomQuestions(
  examSlug: string,
  count = 10,
  categoryId?: number,
): Promise<Question[]> {
  const params = new URLSearchParams();
  params.set("count", String(count));
  if (categoryId) params.set("category_id", String(categoryId));

  const res = await request<Question[]>(
    `/exams/${examSlug}/questions/random?${params.toString()}`,
  );
  return res.data ?? [];
}

export async function generateExplanation(examSlug: string, id: number): Promise<Question> {
  const res = await request<Question>(`/exams/${examSlug}/questions/${id}/explanation`, {
    method: "POST",
  });
  return res.data!;
}

export async function getCategories(examSlug: string): Promise<Category[]> {
  const res = await request<Category[]>(`/exams/${examSlug}/categories`);
  return res.data ?? [];
}

// ─── Tests (exam-scoped) ─────────────────────────────────────────────────────

export async function createTest(
  examSlug: string,
  data: CreateTestRequest,
): Promise<{ session: TestSession; questions: Question[] }> {
  const res = await request<{ session: TestSession; questions: Question[] }>(
    `/exams/${examSlug}/tests`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
  return res.data!;
}

export async function getTestHistory(
  examSlug: string,
  questionNumber?: number,
): Promise<TestSession[]> {
  const params = new URLSearchParams();
  if (questionNumber) params.set("question_number", String(questionNumber));
  const qs = params.toString();
  const res = await request<TestSession[]>(`/exams/${examSlug}/tests${qs ? `?${qs}` : ""}`);
  return res.data ?? [];
}

export async function getTestSession(examSlug: string, id: string): Promise<{ session: TestSession; answers: UserAnswer[]; questions?: Question[] }> {
  const res = await request<{ session: TestSession; answers: UserAnswer[]; questions?: Question[] }>(`/exams/${examSlug}/tests/${id}`);
  return res.data!;
}

export async function submitAnswer(
  examSlug: string,
  testId: string,
  data: SubmitAnswerRequest,
): Promise<UserAnswer> {
  const res = await request<UserAnswer>(`/exams/${examSlug}/tests/${testId}/answers`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.data!;
}

export async function completeTest(examSlug: string, testId: string): Promise<{ session: TestSession; answers: UserAnswer[] }> {
  const res = await request<{ session: TestSession; answers: UserAnswer[] }>(`/exams/${examSlug}/tests/${testId}/complete`, {
    method: "POST",
  });
  return res.data!;
}

// ─── Analytics (exam-scoped) ─────────────────────────────────────────────────

export async function getAnalyticsSummary(examSlug: string): Promise<AnalyticsSummary> {
  const res = await request<AnalyticsSummary>(`/exams/${examSlug}/analytics/summary`);
  return res.data!;
}

export async function getCategoryStats(examSlug: string): Promise<CategoryStats[]> {
  const res = await request<CategoryStats[]>(`/exams/${examSlug}/analytics/categories`);
  return res.data ?? [];
}

export async function getWeakQuestions(examSlug: string, limit = 10): Promise<WeakQuestion[]> {
  const res = await request<WeakQuestion[]>(`/exams/${examSlug}/analytics/weak?limit=${limit}`);
  return res.data ?? [];
}

export async function getProgress(examSlug: string, days = 30): Promise<ProgressPoint[]> {
  const res = await request<ProgressPoint[]>(`/exams/${examSlug}/analytics/progress?days=${days}`);
  return res.data ?? [];
}

// ─── AI Question Generation (admin) ──────────────────────────────────────────

export async function generateQuestions(
  examSlug: string,
  data: GenerateQuestionsRequest,
): Promise<GenerateQuestionsResult> {
  const res = await request<GenerateQuestionsResult>(
    `/admin/exams/${examSlug}/questions/generate`,
    { method: "POST", body: JSON.stringify(data) },
  );
  return res.data!;
}

export async function getDraftQuestions(
  examSlug: string,
  page = 1,
): Promise<{ questions: Question[]; meta: PaginationMeta }> {
  const res = await request<Question[]>(
    `/admin/exams/${examSlug}/questions/drafts?page=${page}`,
  );
  return { questions: res.data ?? [], meta: res.meta! };
}

export async function publishQuestion(examSlug: string, id: number): Promise<Question> {
  const res = await request<Question>(
    `/admin/exams/${examSlug}/questions/${id}/publish`,
    { method: "PUT" },
  );
  return res.data!;
}

// ─── Submissions ─────────────────────────────────────────────────────────────

export async function createSubmission(
  examSlug: string,
  data: SubmitQuestionRequest,
): Promise<Submission> {
  const res = await request<Submission>(`/exams/${examSlug}/submissions`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.data!;
}

export async function getMySubmissions(): Promise<Submission[]> {
  const res = await request<Submission[]>("/my/submissions");
  return res.data ?? [];
}

export async function getAdminSubmissions(): Promise<Submission[]> {
  const res = await request<Submission[]>("/admin/submissions");
  return res.data ?? [];
}

export async function reviewSubmission(
  id: string,
  data: ReviewSubmissionRequest,
): Promise<void> {
  await request(`/admin/submissions/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ─── Reports ────────────────────────────────────────────────────────────────

export async function createReport(
  examSlug: string,
  data: CreateReportRequest,
): Promise<Report> {
  const res = await request<Report>(`/exams/${examSlug}/reports`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.data!;
}

export async function getMyReports(): Promise<Report[]> {
  const res = await request<Report[]>("/my/reports");
  return res.data ?? [];
}

export async function getAdminReports(status?: string): Promise<Report[]> {
  const params = status ? `?status=${status}` : "";
  const res = await request<Report[]>(`/admin/reports${params}`);
  return res.data ?? [];
}

export async function reviewReport(
  id: string,
  data: ReviewReportRequest,
): Promise<void> {
  await request(`/admin/reports/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ─── Upload ──────────────────────────────────────────────────────────────────

export async function getUploadPresignedURL(
  contentType: string,
): Promise<{ upload_url: string; image_url: string }> {
  const res = await request<{ upload_url: string; image_url: string }>(
    "/admin/upload/presigned-url",
    {
      method: "POST",
      body: JSON.stringify({ content_type: contentType }),
    },
  );
  return res.data!;
}

export async function uploadImageToS3(
  file: File,
): Promise<string> {
  const { upload_url, image_url } = await getUploadPresignedURL(file.type);
  await fetch(upload_url, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  return image_url;
}

// ─── Groups ──────────────────────────────────────────────────────────────────

export async function getMyGroups(): Promise<Group[]> {
  const res = await request<Group[]>("/me/groups");
  return res.data ?? [];
}

export async function getMyInvitations(): Promise<GroupInvite[]> {
  const res = await request<GroupInvite[]>("/me/invitations");
  return res.data ?? [];
}

export async function acceptInvitation(gid: string): Promise<void> {
  await request(`/invitations/${gid}/accept`, { method: "POST" });
}

export async function rejectInvitation(gid: string): Promise<void> {
  await request(`/invitations/${gid}/reject`, { method: "POST" });
}

export async function getGroup(gid: string): Promise<Group> {
  const res = await request<Group>(`/groups/${gid}`);
  return res.data!;
}

export async function getGroupMembers(gid: string): Promise<GroupMember[]> {
  const res = await request<GroupMember[]>(`/groups/${gid}/members`);
  return res.data ?? [];
}

export async function getGroupLeaderboard(
  gid: string,
  opts: {
    period?: LeaderboardPeriod;
    from?: string;
    to?: string;
    exam?: string;
  } = {},
): Promise<LeaderboardEntry[]> {
  const params = new URLSearchParams();
  if (opts.period) params.set("period", opts.period);
  if (opts.from) params.set("from", opts.from);
  if (opts.to) params.set("to", opts.to);
  if (opts.exam) params.set("exam", opts.exam);
  const q = params.toString();
  const res = await request<LeaderboardEntry[]>(
    `/groups/${gid}/leaderboard${q ? `?${q}` : ""}`,
  );
  return res.data ?? [];
}

export async function updateGroup(
  gid: string,
  data: UpdateGroupRequest,
): Promise<Group> {
  const res = await request<Group>(`/groups/${gid}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return res.data!;
}

export async function sendGroupInvites(
  gid: string,
  emails: string[],
): Promise<InviteByEmailResult> {
  const res = await request<InviteByEmailResult>(`/groups/${gid}/invites`, {
    method: "POST",
    body: JSON.stringify({ emails }),
  });
  return res.data!;
}

export async function removeGroupMember(
  gid: string,
  uid: string,
): Promise<void> {
  await request(`/groups/${gid}/members/${uid}`, { method: "DELETE" });
}

export async function changeMemberRole(
  gid: string,
  uid: string,
  role: "admin" | "member",
): Promise<void> {
  await request(`/groups/${gid}/members/${uid}/role`, {
    method: "POST",
    body: JSON.stringify({ role }),
  });
}

// Admin
export async function getAdminGroups(): Promise<Group[]> {
  const res = await request<Group[]>("/admin/groups");
  return res.data ?? [];
}

export async function createGroup(data: CreateGroupRequest): Promise<Group> {
  const res = await request<Group>("/admin/groups", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.data!;
}

export async function deleteGroup(gid: string): Promise<void> {
  await request(`/admin/groups/${gid}`, { method: "DELETE" });
}

export async function submitGroupCapInquiry(
  payload: GroupCapInquiryPayload,
): Promise<void> {
  await request("/inquiries/group-cap", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export { ApiError };
